import React from 'react';
import { TableRow, TableCell, IconButton, Box, CircularProgress, Tooltip } from '@mui/material';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { tokens, withAlpha } from '../../Common/designTokens';

const SelectedOrderTableRow = ({
  rowData,
  selectedColumns,
  selectedRung,
  showPreviewOnly,
  onRowClick,
  removeOrder,
  formatValue,
  isNumericOrDate,
  isBuySellColumn,
  isTypeLikeColumn,
  getColumnMaxWidth,
  cellRenderer,
  styles,
  pendingRequests = {},
  pendingCalcUUIDs = [],
  timeoutMessages = {}
}) => {
  const { rowHeight, cellPadding, TABLE_BG_COLOR, ROW_HIGHLIGHT_COLOR } = styles;
  const rungIndex = parseInt(rowData.rungIndex);

  // Check if this row has a pending request
  const rowRequest = pendingRequests[rungIndex];
  // Check if this row has a pending request that is still in progress
  const rowHasPendingRequest = rowRequest && pendingCalcUUIDs.includes(rowRequest.uuid);
  // We still track timeout status even though we don't show special UI for it
  const rowHasTimedOut = timeoutMessages[rungIndex];
  
  // Check if this order is editable
  const isEditable = rowData.isEditable !== false;

  return (
    <TableRow
      sx={{
        cursor: "default",
        height: rowHeight,
        backgroundColor: rungIndex === selectedRung ? ROW_HIGHLIGHT_COLOR : TABLE_BG_COLOR,
        "&:hover": { backgroundColor: tokens.colors.defaultGridHoverColor },
        transition: "background-color 0.2s",
        "&.MuiTableRow-root": {
          backgroundColor: rungIndex === selectedRung ? ROW_HIGHLIGHT_COLOR : TABLE_BG_COLOR,
        },
      }}
      onClick={onRowClick ? () => onRowClick(rowData, rungIndex) : undefined}
    >
      {!showPreviewOnly && (
        <TableCell
          sx={{
            height: rowHeight,
            padding: cellPadding,
            textAlign: "center",
            width: "60px",
            backgroundColor: TABLE_BG_COLOR,
            isolation: "isolate",
            position: 'sticky',
            left: 0,
            zIndex: 2,
          }}
        >
          <Tooltip title={isEditable ? "Remove order" : "Cannot remove completed order"}>
            <span>
              <IconButton
                color="error"
                size="small"
                disabled={!isEditable}
                onClick={(e) => {
                  e.stopPropagation();
                  if (isEditable) {
                    removeOrder(parseInt(rowData.rungIndex));
                  }
                }}
                sx={{ 
                  padding: '4px',
                  '&.Mui-disabled': {
                    color: withAlpha(tokens.palette.danger, 0.3)
                  }
                }}
              >
                <DeleteOutlineIcon fontSize="small" />
              </IconButton>
            </span>
          </Tooltip>
        </TableCell>
      )}

      <TableCell sx={{ 
        height: rowHeight, 
        padding: cellPadding, 
        color: tokens.colors.fontColour, 
        backgroundColor: "transparent", 
        typography: 'body1', 
        overflow: "hidden", 
        whiteSpace: "nowrap", 
        textOverflow: "ellipsis", 
        isolation: "isolate", 
        textAlign: "right",
        ...(showPreviewOnly && { position: 'sticky', left: 0, zIndex: 2 }),
      }}>
        <span style={{ 
          display: "block", 
          overflow: "hidden", 
          textOverflow: "ellipsis", 
          whiteSpace: "nowrap", 
          lineHeight: rowHeight, 
          position: "relative", 
          zIndex: 2, 
          textAlign: "right" 
        }}>
          {rungIndex + 1}
        </span>
      </TableCell>

      {selectedColumns
        .filter(col => !["minQty", "maxQty"].includes(col.key))
        .map((column) => {
          const isBuy = rowData[column.key] === "BUY";
          const isSell = rowData[column.key] === "SELL";
          const value = formatValue(rowData[column.key], column.key);
          const isLoadingCell = rowHasPendingRequest && ["accruedInterest", "ytm"].includes(column.key);
          const textColor = isSell ? tokens.colors.sellFontColor : (isBuy ? tokens.colors.buyFontColor : tokens.colors.fontColour);

          return (
            <TableCell
              key={column.key}
              sx={{
                height: rowHeight,
                padding: cellPadding,
                overflow: "hidden",
                whiteSpace: "nowrap",
                textOverflow: "ellipsis",
                textAlign: isBuySellColumn(column.key) ? "center" : isTypeLikeColumn(column.key) ? "left" : isNumericOrDate(column.key) ? "right" : "left",
                color: textColor,
                fontWeight: isSell || isBuy ? 700 : 400,
                position: "relative",
                isolation: "isolate",
                minWidth: isNumericOrDate(column.key) ? "90px" : "120px",
                maxWidth: getColumnMaxWidth(column.key),
                backgroundColor: "transparent",
              }}
            >
              {isLoadingCell ? (
                <Box sx={{ display: 'flex', justifyContent: 'flex-end', alignItems: 'center', height: '100%' }}>
                  <CircularProgress size={16} />
                </Box>
              ) : (
                <span
                  style={{
                    display: "block",
                    overflow: "hidden",
                    textOverflow: "ellipsis",
                    whiteSpace: "nowrap",
                    textAlign: isBuySellColumn(column.key)
                      ? "center"
                      : isTypeLikeColumn(column.key)
                      ? "left"
                      : isNumericOrDate(column.key)
                      ? "right"
                      : "left",
                    lineHeight: rowHeight,
                    position: "relative",
                    zIndex: 2,
                    width: "100%",
                    color: textColor,
                    fontWeight: isSell || isBuy ? 700 : 400,
                  }}
                  title={value}
                >
                  {cellRenderer(rowData, column, value)}
                </span>
              )}
            </TableCell>
          );
        })}
    </TableRow>
  );
};

export default SelectedOrderTableRow;
